import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { provideMsal } from './identity';
import { NgxsModule } from '@ngxs/store';
import { AppDataState } from './shared/stores/app-data.state';
import { environment } from '@env/environment';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Constants } from '@shared/constants';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DynamicPipe } from '@shared/pipes/dynamic-pipe';
import { YearMonth } from '@shared/pipes/year-month';
import { CountryCodeTranslate } from '@shared/pipes/country-code-translate';
import { BooleanYesNoTranslate } from './shared/pipes/boolean-yes-no-translate';
import { LocaleDate } from './shared/pipes/locale-date';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { CommonDataState } from './shared/stores/common-data.state';
import { MyErrorHandler } from './shared/services/error-handler';
import { LocaleTime } from './shared/pipes/locale-time';
import { LocaleDateTime } from './shared/pipes/locale-date-time';
import { TrimLeadingZero } from './shared/pipes/trim-leading-zero';
import { CountryCodesTranslate } from './shared/pipes/country-codes-translate';
import { SubRegionNamesTranslate } from './shared/pipes/sub-region-names-translate';
import { TerminalNamesTranslate } from './shared/pipes/terminal-names-translate';
import { OnlyNumberDirective } from './shared/directive/only-number.directive';
import { TerminalIdsTranslate } from './shared/pipes/terminal-ids-translate';
import { TerminalIdNamesTranslate } from './shared/pipes/terminal-id-names-translate';
import { BooleanYesNoEmptyTranslate } from './shared/pipes/boolean-yes-no-empty-translate';
import { ImportResultErrorMessageTranslate } from './shared/pipes/import-result-error-message-translate';
import { BusinsessTypesTransform } from './shared/pipes/business-types-transform';
import { DisableDeleteButtonDirective } from './shared/directive/disable-delete-button.directive';
import { DisableEditButtonDirective } from './shared/directive/disable-edit-button.directive';
import { DynamicTranslate } from './shared/pipes/dynamic-translate';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return isDevMode() ? new TranslateHttpLoader(httpClient) : new TranslateHttpLoader(httpClient, '/assets/i18n/', `-${environment.hash}.json`);
  // return new TranslateHttpLoader(httpClient)
}

export function translateFactory(translate: TranslateService) {
  return async () => {
    translate.setDefaultLang(translate.getBrowserCultureLang());
    translate.use(translate.getBrowserCultureLang());

    return new Promise<void>(resolve => {
      translate.onLangChange.subscribe(() => {
        resolve();
      });
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideClientHydration(),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideMsal(),
    DynamicPipe,
    YearMonth,
    ImportResultErrorMessageTranslate,
    CountryCodeTranslate,
    CountryCodesTranslate,
    BooleanYesNoTranslate,
    BooleanYesNoEmptyTranslate,
    DynamicTranslate,
    BusinsessTypesTransform,
    LocaleDate,
    LocaleDateTime,
    LocaleTime,
    TrimLeadingZero,
    SubRegionNamesTranslate,
    TerminalNamesTranslate,
    TerminalIdsTranslate,
    TerminalIdNamesTranslate,
    OnlyNumberDirective,
    DisableDeleteButtonDirective,
    DisableEditButtonDirective,
    { provide: ErrorHandler, useClass: MyErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: translateFactory,
      deps: [TranslateService],
      multi: true
    },
    importProvidersFrom(
      NgxsFormPluginModule.forRoot(),
      TranslateModule.forRoot({
        defaultLanguage: Constants.appDefaultLocale,
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      NgxsModule.forRoot([AppDataState, CommonDataState],
        {
          developmentMode: !environment.production,
          selectorOptions: {
            suppressErrors: false,
            injectContainerState: false
          }
        }
      ),
      // devtools always last
      NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }))
  ]
};
